import React, { useEffect, useMemo, useState } from 'react';
import { MenuProps, Skeleton, Space, Switch, Tooltip } from 'antd';
import './ReceptionistSider.scss';
import { SiderMenuProps } from '@/interfaces/IDashboardSider';

import { useAppSelector } from '@/store/hooks/useAppSelector';
import {
	getLoggedInUser,
	getResellerUserFeaturesGating,
	getResellerUserFeaturesVisibility,
	getSelectedAgentId,
	getSelectedAgent,
	getUserInstructionSteps,
	getInboundAgents,
	getOutboundAgents,
} from '@/store/account/selector';
import { useMobileDeviceCheck } from 'src/common/hooks/useMobile';
import { getResellerData } from '@/store/reseller/selector';
import { useQuery } from '@tanstack/react-query';
import { getResellerLogo } from '@/api/unbrandedReceptionist';
import {
	ExclamationCircleOutlined,
	BookOutlined,
	HomeOutlined,
	UserOutlined,
	DashboardOutlined,
	KeyOutlined,
	SettingOutlined,
	BankOutlined,
	CreditCardOutlined,
	CustomerServiceOutlined,
} from '@ant-design/icons';
import CommonSider from '@/components/Sider/Sider';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { setCurrentPage, setSelectedAgent, setSelectedAgentId } from '@/store/account/slice';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { setResellerView } from '@/store/reseller/slice';

import { sidebarItems as items } from 'src/enums/ESideBarMenuItems';
import { getFeatureFlags } from '@/store/app/selector';
import { DESKTOP_LOGO_S3_URL, MOBILE_LOGO_S3_URL, POSTHOG_FEATURE_FLAGS } from 'src/constants';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import LocalStorageUtility from '@/utils/localStorage';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { resetTranslationCookies } from '@/utils/translation';
import { AppRoutes } from 'src/enums/ERoutes';
import { useNavigate } from 'react-router-dom';
import { User } from '@/types/TAccounts';

type MenuItem = {
	key: string;
	icon?: React.ReactNode;
	label: string;
	children?: MenuItem[];
};

const ReceptionistSider: React.FC<SiderMenuProps> = ({
	activeMenuItem,
	onMenuSelect,
	menuRef,
	overviewRef,
	logsRef,
	className = '',
	debrand,
	setOpenedOnboarding,
	crmRef,
	openedOnboarding,
	tourOpen,
	hidden = false,
}) => {
	const instructionSteps = useAppSelector(getUserInstructionSteps);
	const { isMobile } = useMobileDeviceCheck();
	const isTimezoneFlagEnabled = useFeatureFlag('feat-global-timezone');
	const isGohighlevelIntegrationEnabled = useFeatureFlag(FeatureFlag.GOHIGHLEVEL_INTEGRATION);
	const isCalendarEventsEnabled = useFeatureFlag(FeatureFlag.CALENDAR_EVENTS);
	const isFormResponsesEnabled = useFeatureFlag(FeatureFlag.FORM_RESPONSES_PAGE);
	const betaInbox = useFeatureFlag(FeatureFlag.BETA_INBOX);
	const currentReseller = useAppSelector(getResellerData);
	const [openKeys, setOpenKeys] = useState<string[]>([]);
	const loggedInUser = useAppSelector(getLoggedInUser);
	const dispatch = useAppDispatch();
	const clientHiddenFeatures = useAppSelector(getResellerUserFeaturesVisibility) || [];
	const clientFeaturesGating = useAppSelector(getResellerUserFeaturesGating) || [];
	const featureFlags = useAppSelector(getFeatureFlags);
	const registrationFeatureFlagKey = featureFlags.ONBOARDING_REGISTRATION_REVAMP;
	const newRegistrationFlow =
		registrationFeatureFlagKey ===
		POSTHOG_FEATURE_FLAGS.ONBOARDING_REGISTRATION_REVAMP.variants.NEW;
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const inboundAgents = useAppSelector(getInboundAgents);
	const outboundAgents = useAppSelector(getOutboundAgents);
	const navigate = useNavigate();

	// Memoize the disallowed items
	const disallowedDebrandedItems = useMemo(() => {
		const baseItems = [
			ReceptionistDashboardSiderItem.CONTACT_SUPPORT,
			ReceptionistDashboardSiderItem.SUBSCRIPTION_MANAGEMENT,
			ReceptionistDashboardSiderItem.EXPLORE_FEATURES,
			ReceptionistDashboardSiderItem.DASHBOARD,
		];

		if (
			!currentReseller?.resellerView &&
			(clientHiddenFeatures.length > 0 || clientFeaturesGating.length > 0)
		) {
			const resellerHiddenItems: ReceptionistDashboardSiderItem[] = items
				.filter(
					(item: any) =>
						item !== null && item.key && clientHiddenFeatures.includes(item.key as string),
				)
				.filter((item): item is { key: string } => item !== null && item !== undefined)
				.map((item) => item.key as ReceptionistDashboardSiderItem);

			return [...baseItems, ...resellerHiddenItems];
		}

		return baseItems;
	}, [clientHiddenFeatures, clientFeaturesGating, currentReseller?.resellerView]);

	// Memoize the menu items with all conditions
	const menuItems = useMemo(() => {
		let baseItems = [...items];

		if (betaInbox) {
			baseItems = baseItems.filter((item) => item?.key !== ReceptionistDashboardSiderItem.LOGS);
		} else {
			baseItems = baseItems.filter((item) => item?.key !== ReceptionistDashboardSiderItem.ALL_LOGS);
		}

		if (!isCalendarEventsEnabled) {
			baseItems = baseItems.filter((item) => item?.key !== ReceptionistDashboardSiderItem.CALENDAR);
		}

		if (!isFormResponsesEnabled) {
			baseItems = baseItems.filter((item) => item?.key !== ReceptionistDashboardSiderItem.TICKETS);
		}

		// Add Account and Dashboard tabs if there are fewer than 2 agents
		if (inboundAgents.length < 2 && loggedInUser.userType !== 'SubAccount' && !debrand) {
			// Remove existing Account tab if it exists
			baseItems = baseItems.filter((item) => item?.key !== ReceptionistDashboardSiderItem.ACCOUNT);

			// Add Dashboard at the beginning
			baseItems = [
				{
					key: ReceptionistDashboardSiderItem.DASHBOARD,
					icon: <DashboardOutlined />,
					label: ReceptionistDashboardSiderItem.DASHBOARD,
				},
				...baseItems,
			];

			// Find the index of the CRM tab
			const crmIndex = baseItems.findIndex(
				(item) => item?.key === ReceptionistDashboardSiderItem.CRM,
			);

			// Create the Account tab
			const accountTab = {
				key: ReceptionistDashboardSiderItem.ACCOUNT,
				icon: <UserOutlined />,
				label: ReceptionistDashboardSiderItem.ACCOUNT,
				children: [
					{
						key: ReceptionistDashboardSiderItem.SETTINGS,
						icon: <SettingOutlined />,
						label: ReceptionistDashboardSiderItem.SETTINGS,
					},
					{
						key: ReceptionistDashboardSiderItem.PASSWORD,
						icon: <KeyOutlined />,
						label: ReceptionistDashboardSiderItem.PASSWORD,
					},
					{
						key: ReceptionistDashboardSiderItem.SUBSCRIPTION,
						icon: <BankOutlined />,
						label: ReceptionistDashboardSiderItem.SUBSCRIPTION,
					},
					{
						key: ReceptionistDashboardSiderItem.CREDITS,
						icon: <CreditCardOutlined />,
						label: ReceptionistDashboardSiderItem.CREDITS,
					},
				],
			};

			// Insert the Account tab after CRM
			if (crmIndex !== -1) {
				// baseItems.splice(crmIndex + 1, 0, accountTab);
			} else {
				// If CRM tab is not found, add Account tab at the end
				baseItems.push(accountTab);
			}
		}

		// Add Reseller Plan if user is a reseller client
		if (loggedInUser.resellerClient || currentReseller?.stripe_connect_id) {
			baseItems = [
				...baseItems,
				{
					key: ReceptionistDashboardSiderItem.RESELLER_PLAN,
					icon: React.createElement(BankOutlined),
					label: ReceptionistDashboardSiderItem.RESELLER_PLAN,
				},
			];

			// take out disallowed items from baseItems
			baseItems = baseItems.filter(
				(item) =>
					item &&
					item.key &&
					!disallowedDebrandedItems.includes(item.key as ReceptionistDashboardSiderItem),
			);
		}

		// remove Dashboard tab from menu items if user is not on step 6 (or later) of onboarding
		if (loggedInUser?.inboundDemo && selectedAgentId) {
			baseItems = baseItems.filter(
				(item) => item?.key !== ReceptionistDashboardSiderItem.DASHBOARD,
			);
		}

		// Filter out hidden and gated features when not in reseller view
		if (
			!currentReseller?.resellerView &&
			(clientHiddenFeatures.length > 0 || clientFeaturesGating.length > 0)
		) {
			baseItems = baseItems.filter(
				(item) => item && item.key && !clientHiddenFeatures.includes(item.key as string),
			);
			const allFilteredTabs = baseItems.filter(
				(item) => item && item.key && !clientFeaturesGating.includes(item.key as string),
			);
			const currentTab = allFilteredTabs[0];

			// Update current page if needed
			if (currentTab) {
				// If currentTab has children, navigate to first available child tab
				const menuItem = currentTab as { children?: { children?: { key: string }[] }[] };
				if (menuItem.children?.[0]?.children?.[0]?.key) {
					dispatch(
						setCurrentPage(menuItem.children[0].children[0].key as ReceptionistDashboardSiderItem),
					);
				} else {
					dispatch(setCurrentPage(currentTab.key as ReceptionistDashboardSiderItem));
				}
			}
		}

		if (!loggedInUser.inboundDemo) {
			const hireAIReceptionistItem: any = baseItems.find(
				(item) => item?.key === ReceptionistDashboardSiderItem.HIRE_AI_RECEPTIONIST,
			);
			if (hireAIReceptionistItem) {
				baseItems = baseItems.filter(
					(item) => item?.key !== ReceptionistDashboardSiderItem.HIRE_AI_RECEPTIONIST,
				);
			}
		}

		// Filter for demo users
		if (
			loggedInUser?.inboundDemo &&
			!selectedAgent?.provisionedNumber &&
			selectedAgentId &&
			inboundAgents.length >= 2
		) {
			baseItems = baseItems.filter(
				(item) =>
					item !== null && item !== undefined && item.key !== ReceptionistDashboardSiderItem.HOME,
			);
		} else {
			if (!newRegistrationFlow && selectedAgentId) {
				baseItems = baseItems.filter(
					(item): item is NonNullable<typeof item> =>
						item !== null &&
						item !== undefined &&
						item.key !== ReceptionistDashboardSiderItem.GET_STARTED,
				);
			}
		}

		const accountTab = baseItems.find(
			(item) => item?.key === ReceptionistDashboardSiderItem.ACCOUNT,
		);
		if (!accountTab || !('children' in accountTab)) {
			return baseItems;
		}

		if (!isTimezoneFlagEnabled && selectedAgentId) {
			accountTab.children = accountTab.children?.filter(
				(item) => item?.key !== ReceptionistDashboardSiderItem.PREFERENCES,
			);
		}

		if ((loggedInUser?.userType === 'SubAccount' || debrand) && !selectedAgentId) {
			accountTab.children = accountTab.children?.filter(
				(item) =>
					item?.key !== ReceptionistDashboardSiderItem.SUBSCRIPTION &&
					item?.key !== ReceptionistDashboardSiderItem.USAGE,
			);
		}

		if (!accountTab.children) {
			return baseItems;
		}

		if (
			(loggedInUser.inboundSubscriptionId &&
				loggedInUser.inboundSubscriptionStatus !== 'canceled') ||
			(loggedInUser.outboundSubscriptionId &&
				loggedInUser.outboundSubscriptionStatus !== 'canceled' &&
				!accountTab.children?.some(
					(child) => child?.key === ReceptionistDashboardSiderItem.CREDITS,
				))
		) {
			// Only add Credits tab if it doesn't already exist
			if (
				!accountTab.children.some((child) => child?.key === ReceptionistDashboardSiderItem.CREDITS)
			) {
				accountTab.children.push({
					key: ReceptionistDashboardSiderItem.CREDITS,
					icon: React.createElement(CreditCardOutlined),
					label: ReceptionistDashboardSiderItem.CREDITS,
				});
			}
		}

		return baseItems;
	}, [
		items,
		loggedInUser.resellerClient,
		loggedInUser.inboundSubscriptionId,
		loggedInUser.inboundSubscriptionStatus,
		loggedInUser.outboundSubscriptionId,
		loggedInUser.outboundSubscriptionStatus,
		loggedInUser.inboundDemo,
		currentReseller?.resellerView,
		clientHiddenFeatures,
		clientFeaturesGating,
		disallowedDebrandedItems,
		debrand,
		dispatch,
		isGohighlevelIntegrationEnabled,
		isCalendarEventsEnabled,
		isFormResponsesEnabled,
	]);

	// Memoize support buttons
	const supportButtons = useMemo(() => {
		const buttons = [];

		if (currentReseller?.customButton1Label && currentReseller?.customButton1URL) {
			buttons.push({
				key: ReceptionistDashboardSiderItem.RESELLER_SUPPORT_BUTTON_1,
				icon: <ExclamationCircleOutlined />,
				label: currentReseller?.customButton1Label,
			});
		}

		if (currentReseller?.customButton2Label && currentReseller?.customButton2URL) {
			buttons.push({
				key: ReceptionistDashboardSiderItem.RESELLER_SUPPORT_BUTTON_2,
				icon: <BookOutlined />,
				label: currentReseller?.customButton2Label,
			});
		}

		return buttons;
	}, [currentReseller]);

	const { data, isPending: isLogoLoading } = useQuery({
		queryKey: ['reseller-logo'],
		queryFn: () => getResellerLogo({ key: currentReseller?.logo as string }),
		enabled: !!(debrand && currentReseller?.logo),
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const resellerLogoUrl = useMemo(() => {
		if (!data) {
			return;
		}
		return URL.createObjectURL(data);
	}, [data]);

	const handleClick: MenuProps['onClick'] = (e) => {
		if (e.key === 'Logout') {
			LocalStorageUtility.clearLocalData();
			resetTranslationCookies();

			if (window.churnkey) {
				window.churnkey.clearState?.();
			}
		} else if (e.key === 'back-to-dashboard') {
			// Handle back to dashboard click
			navigate(AppRoutes.DASHBOARD);
			dispatch(setSelectedAgent({ user: null }));
			dispatch(setSelectedAgentId({ selectedAgentId: null }));
			dispatch(setCurrentPage(ReceptionistDashboardSiderItem.DASHBOARD));
		} else if (inboundAgents.find((agent) => agent.number === e.key)) {
			return;
		} else {
			if (clientFeaturesGating.includes(e.key as string) && !currentReseller?.resellerView) {
				dispatch(openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }));
				return;
			}

			onMenuSelect(e.key as ReceptionistDashboardSiderItem);

			// Handle reseller support buttons
			if (e.key === ReceptionistDashboardSiderItem.RESELLER_SUPPORT_BUTTON_1) {
				window.open(currentReseller?.customButton1URL, '_blank');
			}

			if (e.key === ReceptionistDashboardSiderItem.RESELLER_SUPPORT_BUTTON_2) {
				window.open(currentReseller?.customButton2URL, '_blank');
			}
		}
	};

	const onOpenChange: MenuProps['onOpenChange'] = (keys) => {
		setOpenKeys(keys);
	};

	useEffect(() => {
		const parentKey: string | undefined = items.reduce<string | undefined>((acc, item) => {
			if (acc) {
				return acc;
			}
			if (item && 'children' in item && Array.isArray(item.children)) {
				const matchingChild = item.children?.find((child) => child.key === activeMenuItem);

				if (matchingChild) {
					return item.key as string;
				}
			}
			return undefined;
		}, undefined);

		if (parentKey && !openKeys.includes(parentKey)) {
			setOpenKeys([parentKey]);
		}
	}, [activeMenuItem, openKeys, items]);

	// Handle refs setup
	useEffect(() => {
		const setRefs = () => {
			if (menuRef.current) {
				const menuItems = menuRef.current.querySelectorAll('.ant-menu-item');
				const subMenuItems = menuRef.current.querySelectorAll('.ant-menu-submenu');

				const configureSubTab = subMenuItems[0];
				const crmMenu = menuItems[8];
				const logsSubMenu = subMenuItems[1];

				if (configureSubTab) {
					overviewRef.current = configureSubTab as HTMLElement;
				}
				if (logsSubMenu) {
					logsRef.current = logsSubMenu as HTMLElement;
				}
				if (crmMenu) {
					crmRef.current = crmMenu as HTMLElement;
				}
			}
		};

		setRefs();

		const observer = new MutationObserver(setRefs);
		if (menuRef.current) {
			observer.observe(menuRef.current, { childList: true, subtree: true });
		}

		return () => observer.disconnect();
	}, [instructionSteps, menuRef, overviewRef, logsRef, crmRef]);

	const baseClass = 'sider-container';
	const logo = (
		<div className={`${baseClass}_logo`}>
			{debrand ? (
				<div>
					{!!currentReseller &&
						!!currentReseller?.totalInboundAgents &&
						!!selectedAgent?.featuresSubAccount && (
							<Tooltip title="It is used to view or edit hidden/gated features of your clients">
								<Space direction="horizontal" size="small">
									<Switch
										size="small"
										checked={currentReseller?.resellerView}
										onChange={(value) => dispatch(setResellerView(value))}
									/>
									<span>
										Reseller View
										{currentReseller?.resellerView ? ' ON' : ' OFF'}
									</span>
								</Space>
							</Tooltip>
						)}
					{isLogoLoading ? (
						<Skeleton.Avatar active size={80} shape="circle" />
					) : resellerLogoUrl ? (
						<div style={{ display: 'flex', justifyContent: 'center' }}>
							<img src={resellerLogoUrl} alt="RLU RCS" width={80} height={80} />
						</div>
					) : (
						<h1>{currentReseller?.businessName}</h1>
					)}
				</div>
			) : isMobile ? (
				<img src={MOBILE_LOGO_S3_URL} alt="ML RCS" height={80} width={80} />
			) : (
				<img src={DESKTOP_LOGO_S3_URL} alt="DL RCS" />
			)}
		</div>
	);

	const getDynamicSidebarItems = (): MenuItem[] => {
		if (menuItems) {
			const items = menuItems.map((item: any) => {
				if (item.key === ReceptionistDashboardSiderItem.GET_STARTED) {
					return null;
				}
				return item;
			});

			const userDashboardRelatedItems =
				loggedInUser.userType === 'SubAccount' ||
				loggedInUser.resellerClient ||
				(inboundAgents.length < 2 && !outboundAgents.length)
					? []
					: [
							{
								key: 'back-to-dashboard',
								icon: <HomeOutlined style={{ fontSize: '16px', marginRight: '8px' }} />,
								label: 'Home',
								className: 'back-to-dashboard',
								style: {
									marginBottom: '8px',
									backgroundColor: '#fafafa',
									borderRadius: '6px',
									transition: 'all 0.2s ease',
								},
							},
							{
								key: 'my-agents',
								icon: <CustomerServiceOutlined />,
								label: 'My Agents',
								className: 'my-agents',
								style: {
									marginBottom: '8px',
									backgroundColor: '#fafafa',
									borderRadius: '6px',
									transition: 'all 0.2s ease',
								},
								children: inboundAgents.map((agent: User) => ({
									key: agent.number,
									label: agent.name || 'My First Agent',
									icon: <UserOutlined />,
									onClick: () => handleOpenAgent(agent.number),
									className: `${selectedAgentId === agent.number ? 'bg-[#e6f4ff]' : 'hover:bg-[#f5f5f5]'}`,
									style: {
										marginBottom: '2px',
										borderRadius: '4px',
										transition: 'all 0.2s ease',
									},
								})),
							},
						];

			return [...userDashboardRelatedItems, ...items];
		}
		return [];
	};

	const handleOpenAgent = (agentNumber: string) => {
		navigate(AppRoutes.INBOUND_AGENT_PORTAL.replace(':agentId', agentNumber));
		dispatch(setSelectedAgentId({ selectedAgentId: agentNumber }));
		dispatch(
			setSelectedAgent({
				user: inboundAgents.find((agent: User) => agent.number === agentNumber) as User,
			}),
		);
	};

	return hidden ? (
		<></>
	) : (
		<CommonSider
			items={[
				...getDynamicSidebarItems()
					.filter(
						(items) => !disallowedDebrandedItems.some((item) => items?.key === item && !!debrand),
					)
					.concat(supportButtons),
			]}
			logo={logo}
			activeMenuItem={activeMenuItem}
			handleClick={handleClick}
			className={baseClass + className}
			openKeys={
				openKeys.length > 0
					? openKeys
					: activeMenuItem === ReceptionistDashboardSiderItem.DASHBOARD
						? [ReceptionistDashboardSiderItem.DASHBOARD]
						: [ReceptionistDashboardSiderItem.RECEPTIONIST]
			}
			onOpenChange={onOpenChange}
			menuRef={menuRef}
			setOpenedOnboarding={setOpenedOnboarding}
			openedOnboarding={openedOnboarding}
			tourOpen={tourOpen}
			debrand={debrand}
			width={250}
		/>
	);
};

export default ReceptionistSider;
